import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  agoraUtc,
  dataCurta,
  loteSugerido,
  montarEnvelopeDaExtracao,
  montarPedidoDeGeracao,
  TIPOS_ITEM,
  type BlocoExtraido,
  type CategoriaResumo,
  type EstadoDaIa,
  type Fonte,
  type Lote,
  type Relatorio,
  type TipoItem,
} from "@noesis/shared";
import { emEnsaio, FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import {
  IconeAbrirMenu,
  IconeAcerto,
  IconeAvancar,
  IconeAviso,
  IconeConferido,
  IconeExcluir,
  IconeExportar,
  IconeIa,
  IconeImportar,
} from "../componentes/icones.js";
import { Contador } from "../componentes/Controles.js";
import { Marca } from "../componentes/Marca.js";
import { CaixaMao } from "../componentes/Tracos.js";
import { SeletorDeCategoria } from "../componentes/SeletorDeCategoria.js";
import { ASSUNTO_DO_ENSAIO, LOTE_DO_ENSAIO } from "../componentes/tour/ensaio.js";
import { useSessao } from "../estado/sessao.js";
import { useTour } from "../estado/tour.js";
import { useTrilhaAtiva } from "../estado/trilhas.js";

const NOME_DO_TIPO_LONGO: Record<TipoItem, string> = {
  multipla: "múltipla",
  certo_errado: "certo ou errado",
  cartao: "cartão",
  cloze: "lacunas",
};

/**
 * Os tipos que o pedido traz marcados de partida (ADR 041): os três que se
 * respondem com um toque. Lacunas pedem teclado, e o primeiro lote é para
 * conhecer o fluxo, não para digitar.
 */
const TIPOS_DE_PARTIDA: readonly TipoItem[] = ["multipla", "certo_errado", "cartao"];

/** "múltipla, certo ou errado e cartão" — a lista em português corrido. */
function listaDeTipos(tipos: readonly TipoItem[]): string {
  const nomes = TIPOS_ITEM.filter((t) => tipos.includes(t)).map((t) => NOME_DO_TIPO_LONGO[t]);
  if (nomes.length === 0) return "o modelo escolhe os tipos";
  return nomes.length === 1 ? nomes[0]! : `${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}`;
}

/**
 * Quantos itens de cada tipo o texto colado traz, para a conferência mostrar
 * ("3 múltipla · 2 certo ou errado · 1 cartão"). É leitura de apoio: o
 * relatório do servidor continua sendo quem decide o que é válido, e texto que
 * não é JSON simplesmente não ganha esta linha.
 */
function contarTipos(texto: string): string | null {
  try {
    const dado = JSON.parse(texto) as { itens?: { tipo?: unknown }[] };
    if (!Array.isArray(dado.itens)) return null;
    const conta = new Map<TipoItem, number>();
    for (const i of dado.itens) {
      if (typeof i?.tipo === "string" && (TIPOS_ITEM as readonly string[]).includes(i.tipo)) {
        conta.set(i.tipo as TipoItem, (conta.get(i.tipo as TipoItem) ?? 0) + 1);
      }
    }
    if (conta.size === 0) return null;
    return TIPOS_ITEM.filter((t) => conta.has(t))
      .map((t) => `${conta.get(t)} ${NOME_DO_TIPO_LONGO[t]}`)
      .join(" · ");
  } catch {
    return null;
  }
}

/**
 * Importar — RN-21 a RN-25, em três passos numerados (ADR 041).
 *
 * 1 · Sobre o que devem ser as questões → 2 · copie o pedido e cole num
 * modelo → 3 · cole a resposta aqui. O arquivo `.json` continua valendo, como
 * caminho secundário para o mesmo campo.
 *
 * O fluxo é sempre **colar → conferir → importar**, e o meio não é opcional:
 * não existe caminho nesta tela que grave sem o relatório ter sido mostrado.
 * Mexer no texto depois de conferir apaga a conferência — o que foi conferido
 * é o que se importa, e nada além.
 */
export function Importar() {
  const trilha = useTrilhaAtiva();
  const navegar = useNavigate();
  const tour = useTour();
  const [colado, setColado] = useState("");
  /** O texto exato que o relatório descreve. */
  const [conferido, setConferido] = useState<string | null>(null);
  const [relatorio, setRelatorio] = useState<Relatorio | null>(null);
  /** De onde veio o que está conferido: nome do arquivo, colado ou gerado. */
  const [origem, setOrigem] = useState("resposta colada");
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [importado, setImportado] = useState<{ lote: string; itens: number; semSessao: boolean } | null>(null);
  const [loteNovo, setLoteNovo] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const entrada = useRef<HTMLInputElement | null>(null);
  const botaoImportar = useRef<HTMLButtonElement | null>(null);
  const botaoOutro = useRef<HTMLButtonElement | null>(null);
  // "Importar questões" numa prova de Fontes chega aqui pela URL (ADR 045).
  const [params, setParams] = useSearchParams();
  const extrair = params.get("extrair");

  const carregarLotes = useCallback(async () => {
    try {
      setLotes((await pedir("lote.listar")).lotes);
    } catch {
      /* sem trilha ativa: o estado guiado abaixo já cobre */
    }
  }, []);

  useEffect(() => {
    if (trilha) void carregarLotes();
  }, [trilha, carregarLotes]);

  const zona = importado ? "importado" : relatorio && conferido !== null ? "conferido" : "vazio";

  // O foco acompanha a zona: quem conferiu chega no Importar, quem importou
  // chega no "importar outro".
  useEffect(() => {
    if (zona === "conferido") botaoImportar.current?.focus();
    if (zona === "importado") botaoOutro.current?.focus();
  }, [zona]);

  function mudarColado(texto: string) {
    setColado(texto);
    if (texto.trim()) tour.avancarSe("colar");
    setErro(null);
    if (conferido !== null && texto !== conferido) {
      setConferido(null);
      setRelatorio(null);
    }
  }

  async function conferir(texto: string, de: string) {
    if (!texto.trim()) return;
    setErro(null);
    setImportado(null);
    setOcupado(true);
    try {
      const r = await pedir("importacao.validar", { corpo: { conteudo: texto } });
      setRelatorio(r);
      setConferido(texto);
      setOrigem(de);
      tour.informar({ itensValidos: r.totalItens });
      tour.avancarSe("conferir");
    } catch (e) {
      setRelatorio(null);
      setConferido(null);
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível conferir.");
    } finally {
      setOcupado(false);
    }
  }

  async function lerArquivo(arquivo: File) {
    if (entrada.current) entrada.current.value = "";
    if (!/\.json$/i.test(arquivo.name) && arquivo.type !== "application/json") {
      setErro(`"${arquivo.name}" não é um .json. Solte o arquivo que o modelo devolveu.`);
      return;
    }
    const texto = await arquivo.text();
    setColado(texto);
    // Confere assim que o arquivo chega: obrigar um segundo toque para ver o
    // relatório só adiaria a má notícia.
    await conferir(texto, arquivo.name);
  }

  /** Volta para o campo. O texto fica: quem volta de um erro quer corrigir. */
  function trocarResposta() {
    setConferido(null);
    setRelatorio(null);
    setImportado(null);
    setErro(null);
  }

  function importarOutro() {
    trocarResposta();
    setColado("");
  }

  async function importar() {
    if (conferido === null || !trilha) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await pedir("importacao.confirmar", {
        corpo: { conteudo: conferido, deslocar: Boolean(relatorio?.colisao) },
      });
      setLotes(r.lotes);
      setLoteNovo(r.loteId);
      tour.avancarSe("importarBtn");
      setColado("");
      setConferido(null);
      setRelatorio(null);
      const feito = { lote: r.relatorio.lote ?? "lote", itens: r.relatorio.totalItens, semSessao: false };
      // No tour o passo seguinte acontece na questão: importou, estuda.
      if (emEnsaio()) {
        try {
          const m = await pedir("estudo.montar", { corpo: { quantidade: trilha.metaDiaria } });
          if (m.montada || m.atual.sessao) {
            navegar("/estudar");
            return;
          }
          feito.semSessao = true;
        } catch {
          /* fica no "Importado." */
        }
      }
      setImportado(feito);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível importar.");
    } finally {
      setOcupado(false);
    }
  }

  if (!trilha) {
    return (
      <Moldura>
        <section className="estado">
          <h2 className="estado__titulo">Nenhuma trilha em uso</h2>
          <p className="estado__texto">
            A importação exige trilha: é ela que recebe os itens, e item sem trilha não pode existir.
          </p>
          <Link className="botao" to="/trilhas">
            Escolher uma trilha
          </Link>
        </section>
      </Moldura>
    );
  }

  // Colisão de ids é o único erro que a confirmação resolve sozinha (RN-22).
  const soColisao = relatorio ? relatorio.erros.length === 1 && relatorio.colisao !== null : false;
  const podeImportar = Boolean(relatorio && (relatorio.ok || soColisao)) && !ocupado;
  const tipos = conferido ? contarTipos(conferido) : null;
  // O lote mais novo primeiro: é o que se acabou de importar.
  const ordenados = [...lotes].sort((a, b) => b.importadoEm.localeCompare(a.importadoEm));

  return (
    <div className="importar-caderno">
      <div className="importar-caderno__colunas">
        <div className="importar-caderno__pedido">
          {extrair && (
            <ExtrairDaFonte
              key={extrair}
              fonteId={extrair}
              trilha={trilha.nome}
              lotes={lotes}
              aoExtrair={(texto) => {
                // A prova entra pela mesma porta do colado: campo do passo 3 e
                // a conferência de sempre. Nada é gravado antes do "importar".
                setColado(texto);
                void conferir(texto, "questões da prova");
              }}
              aoFechar={() => {
                const proximos = new URLSearchParams(params);
                proximos.delete("extrair");
                setParams(proximos, { replace: true });
              }}
            />
          )}

          <PedirAUmModelo
            trilha={trilha.nome}
            lotes={lotes}
            aoGerar={(conteudoGerado, relatorioGerado) => {
              // O gerado entra pela mesma porta do colado: o texto vai para o
              // campo do passo 3 e o relatório para a mesma conferência. Nada
              // é gravado antes do "importar" (RN-21).
              setErro(null);
              setImportado(null);
              setColado(conteudoGerado);
              setConferido(conteudoGerado);
              setRelatorio(relatorioGerado);
              setOrigem("gerado aqui pelo Grifo");
              tour.informar({ itensValidos: relatorioGerado.totalItens });
              tour.avancarSe("conferir");
            }}
          />
        </div>

        <section className="importar-caderno__resposta" aria-labelledby="colar-titulo">
          <h2 className="t-secao" id="colar-titulo">
            3 · Traga a resposta
          </h2>
          <p className="t-ajuda t-2 importar__ajuda">Cole o texto que o modelo devolveu ou solte o arquivo .json aqui.</p>

          <div aria-live="polite">
            {zona === "vazio" && (
              <div
                className={arrastando ? "importar__soltar importar__soltar--sobre" : "importar__soltar"}
                onDragOver={(e) => {
                  if (!e.dataTransfer.types.includes("Files")) return;
                  e.preventDefault();
                  setArrastando(true);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setArrastando(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setArrastando(false);
                  const f = e.dataTransfer.files[0];
                  if (f) void lerArquivo(f);
                }}
              >
                <label className="so-leitor" htmlFor="importar-colado">
                  Resposta do modelo
                </label>
                <textarea
                  id="importar-colado"
                  className={["importar__colar", colado && "importar__colar--cheio", erro && "importar__colar--erro"]
                    .filter(Boolean)
                    .join(" ")}
                  data-tour="colar"
                  value={colado}
                  onChange={(e) => mudarColado(e.target.value)}
                  // No ensaio, tocar no campo já cola a "resposta do modelo": o
                  // tour não depende do que um modelo de fora devolveria.
                  onFocus={() => {
                    if (emEnsaio() && !colado.trim()) mudarColado(LOTE_DO_ENSAIO);
                  }}
                  placeholder="toque e cole a resposta do modelo"
                  spellCheck={false}
                  autoComplete="off"
                  rows={8}
                  aria-invalid={Boolean(erro) || undefined}
                  aria-describedby={erro ? "importar-erro" : undefined}
                />
                <div className="importar__conferir">
                  <p className="importar__arquivo">
                    <Icone glifo={IconeImportar} papel="estado" />
                    <span className="t-ajuda t-2">arraste um .json ou</span>
                    <label className="link-mao">
                      escolha no computador
                      <input
                        ref={entrada}
                        className="so-leitor"
                        type="file"
                        accept=".json,application/json"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void lerArquivo(f);
                        }}
                      />
                    </label>
                  </p>
                  <button
                    className="botao"
                    type="button"
                    data-tour="conferir"
                    disabled={!colado.trim() || ocupado}
                    onClick={() => void conferir(colado, "resposta colada")}
                  >
                    {ocupado ? <Marca girando className="marca--pequena" /> : <Icone glifo={IconeConferido} papel="acao" />}
                    {ocupado ? "Conferindo…" : "Conferir"}
                  </button>
                </div>
                {arrastando && (
                  <div className="importar__soltar-aviso" aria-hidden="true">
                    <Icone glifo={IconeExportar} papel="estado" />
                    <span>solte para conferir</span>
                  </div>
                )}
              </div>
            )}

            {zona === "conferido" && relatorio && (
              <section className="importar__estado" aria-labelledby="relatorio-titulo">
                <div className="importar__estado-topo">
                  <Icone glifo={relatorio.ok || soColisao ? IconeAcerto : IconeAviso} papel="estado" />
                  <div>
                    <h3 className="importar__origem" id="relatorio-titulo">
                      {origem}
                    </h3>
                    <p className="t-ajuda t-2">
                      {relatorio.totalItens} {relatorio.totalItens === 1 ? "item" : "itens"}
                      {relatorio.faixaIds ? ` · ids ${relatorio.faixaIds.inicio}–${relatorio.faixaIds.fim}` : ""}
                    </p>
                  </div>
                </div>
                <RelatorioPrevio r={relatorio} soColisao={soColisao} tipos={tipos} />
                {erro && (
                  <p className="campo__erro" role="alert">
                    {erro}
                  </p>
                )}
                <div className="importar__estado-rodape">
                  <button type="button" className="link-mao link-mao--tinta" onClick={trocarResposta}>
                    {relatorio.ok || soColisao ? "trocar resposta" : "corrigir a resposta"}
                  </button>
                  <button
                    ref={botaoImportar}
                    className="botao"
                    type="button"
                    data-tour="importarBtn"
                    disabled={!podeImportar}
                    onClick={() => void importar()}
                  >
                    <Icone glifo={IconeExportar} papel="acao" />
                    {ocupado
                      ? "Importando…"
                      : soColisao
                        ? "Importar com deslocamento"
                        : relatorio.ok
                          ? `Importar ${relatorio.totalItens} ${relatorio.totalItens === 1 ? "item" : "itens"}`
                          : "Corrija e confira de novo"}
                  </button>
                </div>
              </section>
            )}

            {zona === "importado" && importado && (
              <section className="importar__estado importar__estado--importado" aria-labelledby="importado-titulo">
                <Icone glifo={IconeAcerto} papel="estado" />
                <h3 className="importar__importado" id="importado-titulo">
                  Importado.
                </h3>
                <p className="t-corpo">
                  {importado.itens} {importado.itens === 1 ? "item entrou" : "itens entraram"} em {trilha.nome} como{" "}
                  <strong>{importado.lote}</strong>.{" "}
                  {importado.semSessao ? "Nenhuma sessão para montar agora." : "Já aparecem na próxima sessão."}
                </p>
                <div className="importar__estado-rodape importar__estado-rodape--links">
                  <Link className="link-mao" to="/estudar">
                    estudar agora
                  </Link>
                  <button ref={botaoOutro} type="button" className="link-mao" onClick={importarOutro}>
                    importar outro
                  </button>
                  <button
                    type="button"
                    className="link-mao link-mao--tinta"
                    onClick={() => document.getElementById("lotes")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    ver lote abaixo ↓
                  </button>
                </div>
              </section>
            )}
          </div>

          {zona === "vazio" && erro && (
            <p className="campo__erro importar__erro" id="importar-erro" role="alert">
              {erro}
            </p>
          )}
        </section>
      </div>

      <section className="importar__lotes" id="lotes" aria-labelledby="lotes-titulo">
        <h2 className="t-secao" id="lotes-titulo">
          Lotes desta trilha ({lotes.length})
        </h2>
        {lotes.length === 0 ? (
          <p className="t-ajuda">Nenhum lote ainda. O primeiro entra pelo passo 3.</p>
        ) : (
          <>
            <p className="t-ajuda t-2">
              {lotes.reduce((n, l) => n + l.itensAtuais, 0)} itens no total. Excluir um lote apaga só as questões dele.
            </p>
            <ul className="fontes-folhas">
              {ordenados.map((l) => (
                <li key={l.id}>
                  <FichaDeLote
                    lote={l}
                    novo={l.id === loteNovo}
                    confirmando={excluindo === l.id}
                    aoPedirExclusao={() => setExcluindo(l.id)}
                    aoCancelar={() => setExcluindo(null)}
                    aoConfirmar={async (confirmacaoNome) => {
                      const r = await pedir("lote.excluir", { params: { id: l.id }, corpo: { confirmacaoNome } });
                      setLotes(r.lotes);
                      setExcluindo(null);
                    }}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * As questões de uma prova cadastrada em Fontes, bloco a bloco — ADR 045.
 *
 * Um pedido por bloco, em ordem, e não um pedido só: a prova inteira passaria
 * do teto de tokens por minuto do provedor gratuito, e assim a tela mostra o
 * progresso. Bloco que falha não perde os anteriores — "Tentar de novo"
 * continua de onde parou.
 */
function ExtrairDaFonte({
  fonteId,
  trilha,
  lotes,
  aoExtrair,
  aoFechar,
}: {
  fonteId: string;
  trilha: string;
  lotes: readonly Lote[];
  aoExtrair: (texto: string) => void;
  aoFechar: () => void;
}) {
  const feitos = useRef<BlocoExtraido[]>([]);
  const [lidos, setLidos] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [titulo, setTitulo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [fim, setFim] = useState<{ totalItens: number; semGabarito: number } | null>(null);
  const [tentativa, setTentativa] = useState(0);
  // Os lotes mudam quando a lista carrega; a extração não recomeça por isso.
  const nomesDosLotes = useRef<string[]>([]);
  nomesDosLotes.current = lotes.map((l) => l.nome);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      setErro(null);
      try {
        for (let n = feitos.current.length; ; n += 1) {
          // O último nível de cada categoria já usada é o assunto (ADR 045).
          const assuntos = [
            ...new Set(
              feitos.current.flatMap((f) =>
                f.itens.map((i) => (Array.isArray(i.categoria) ? String(i.categoria.at(-1) ?? "") : "")),
              ),
            ),
          ].filter(Boolean);
          const b = await pedir("ia.extrairBloco", { params: { id: fonteId }, corpo: { bloco: n, assuntos } });
          if (!vivo) return;
          feitos.current.push(b);
          setTitulo(b.titulo);
          setTotal(b.totalBlocos);
          setLidos(feitos.current.length);
          if (feitos.current.length >= b.totalBlocos) break;
        }
        const r = montarEnvelopeDaExtracao(feitos.current, {
          trilha,
          titulo: feitos.current[0]?.titulo ?? "prova",
          lotesExistentes: nomesDosLotes.current,
        });
        setFim({ totalItens: r.totalItens, semGabarito: r.semGabarito });
        if (r.totalItens > 0) aoExtrair(r.texto);
      } catch (e) {
        if (vivo) setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível ler este bloco.");
      }
    })();
    return () => {
      vivo = false;
    };
    // `aoExtrair` muda a cada render do pai; a extração só recomeça por tentativa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fonteId, trilha, tentativa]);

  return (
    <section className="importar__passo importar__extracao" aria-labelledby="extracao-titulo" aria-live="polite">
      <h2 className="t-secao" id="extracao-titulo">
        <Icone glifo={IconeIa} papel="acao" />
        {titulo ? `Questões de "${titulo}"` : "Questões da prova"}
      </h2>

      {erro ? (
        <>
          <p className="campo__erro" role="alert">
            {total ? `Parou no bloco ${lidos + 1} de ${total}: ${erro}` : erro}
          </p>
          <div className="importar__acoes">
            <button className="botao" type="button" onClick={() => setTentativa((t) => t + 1)}>
              Tentar de novo
            </button>
            <button className="botao botao--secundario" type="button" onClick={aoFechar}>
              Cancelar
            </button>
          </div>
        </>
      ) : fim === null ? (
        <>
          <p className="t-corpo">
            {total ? `Lendo o bloco ${Math.min(lidos + 1, total)} de ${total}…` : "Dividindo a prova em blocos…"}
          </p>
          <progress className="importar__progresso" max={total ?? 1} value={total ? lidos : 0} />
          <p className="t-ajuda">
            As questões são copiadas como estão na prova. Cada bloco é uma chamada de IA do seu dia.
          </p>
        </>
      ) : fim.totalItens === 0 ? (
        <div className="estado">
          <p className="estado__texto">
            Nenhuma questão reconhecida no texto desta fonte. Confira em Fontes se a conversão trouxe as questões.
          </p>
          <Link className="botao botao--secundario" to="/acervo?aba=fontes" onClick={aoFechar}>
            Abrir Fontes
          </Link>
        </div>
      ) : (
        <>
          <p className="t-corpo">
            {fim.totalItens} {fim.totalItens === 1 ? "questão extraída" : "questões extraídas"}. Confira na conferência e
            importe.
          </p>
          {fim.semGabarito > 0 && (
            <p className="t-ajuda">
              {fim.semGabarito === fim.totalItens ? "Nenhuma" : `${fim.semGabarito}`} delas{" "}
              {fim.semGabarito === fim.totalItens ? "tinha gabarito na prova" : "sem gabarito na prova"}: a IA resolveu e
              deixou o aviso na explicação de cada uma.
            </p>
          )}
          <div className="importar__acoes">
            <button className="botao botao--secundario" type="button" onClick={aoFechar}>
              Fechar
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo">{children}</div>
    </div>
  );
}

/**
 * O relatório prévio. A ordem é deliberada: primeiro **o que vai entrar**,
 * depois o que bloqueia, depois o que só avisa.
 *
 * Ver os números antes dos problemas é o que permite reconhecer "colei a
 * resposta errada" antes de ler erro nenhum.
 */
function RelatorioPrevio({ r, soColisao, tipos }: { r: Relatorio; soColisao: boolean; tipos: string | null }) {
  return (
    <>
      <dl className="conferencia">
        <div className="conferencia__linha">
          <dt className="t-anotacao-p t-2">Lote</dt>
          <dd className="t-ajuda">{r.lote ?? "—"}</dd>
        </div>
        <div className="conferencia__linha">
          <dt className="t-anotacao-p t-2">Itens</dt>
          <dd className="t-ajuda dado">{r.totalItens}</dd>
        </div>
        {tipos && (
          <div className="conferencia__linha">
            <dt className="t-anotacao-p t-2">Tipos</dt>
            <dd className="t-ajuda">{tipos}</dd>
          </div>
        )}
        {r.trilha && (
          <div className="conferencia__linha">
            <dt className="t-anotacao-p t-2">Trilha no arquivo</dt>
            <dd className="t-ajuda">{r.trilha}</dd>
          </div>
        )}
        <div className="conferencia__linha">
          <dt className="t-anotacao-p t-2">Faixa de ids</dt>
          <dd className="t-ajuda dado">
            {r.faixaIds ? `${r.faixaIds.inicio}–${r.faixaIds.fim}` : "—"}
            {r.colisao ? " (deslocada)" : ""}
          </dd>
        </div>
        <div className="conferencia__linha">
          <dt className="t-anotacao-p t-2">Categorias novas</dt>
          <dd className="t-ajuda dado">{r.categoriasNovas.length}</dd>
        </div>
      </dl>

      {r.categoriasNovas.length > 0 && (
        <details className="texto-base">
          <summary className="texto-base__resumo">Quais categorias serão criadas</summary>
          <ul className="texto-base__corpo lista-simples">
            {r.categoriasNovas.map((c) => (
              <li key={c.join("/")}>{c.join(" › ")}</li>
            ))}
          </ul>
        </details>
      )}

      {r.erros.length > 0 && (
        <ListaDeProblemas
          titulo={soColisao ? "Precisa da sua decisão" : `${r.erros.length} erro(s) bloqueiam`}
          problemas={r.erros}
          tom={soColisao ? "atencao" : "erro"}
        />
      )}

      {r.avisos.length > 0 && (
        <ListaDeProblemas titulo={`${r.avisos.length} aviso(s) — não bloqueiam`} problemas={r.avisos} tom="atencao" />
      )}

      {r.ok && r.avisos.length === 0 && (
        <p className="t-ajuda t-verde conferencia__ok">
          <Icone glifo="check" papel="acao" tamanho="p" />
          Nada a corrigir.
        </p>
      )}
    </>
  );
}

function ListaDeProblemas({
  titulo,
  problemas,
  tom,
}: {
  titulo: string;
  problemas: { onde: string; mensagem: string; comoResolver: string }[];
  tom: "erro" | "atencao";
}) {
  return (
    <section className={`problemas problemas--${tom}`}>
      <h3 className="feedback__veredito">{titulo}</h3>
      <ul className="pilha">
        {problemas.map((p, i) => (
          <li className="problema" key={`${p.onde}-${i}`}>
            <p className="problema__onde dado">{p.onde}</p>
            <p className="problema__mensagem">{p.mensagem}</p>
            {/* A saída, sempre. Diagnóstico sem saída só informa que a noite acabou. */}
            <p className="problema__saida">{p.comoResolver}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Um lote como folha, no mesmo desenho das fontes. Excluir acontece dentro da
 * própria folha e continua exigindo o nome digitado (invariante 11): é o nome
 * que diz qual dos lotes parecidos está indo embora.
 */
function FichaDeLote({
  lote,
  novo,
  confirmando,
  aoPedirExclusao,
  aoCancelar,
  aoConfirmar,
}: {
  lote: Lote;
  novo: boolean;
  confirmando: boolean;
  aoPedirExclusao: () => void;
  aoCancelar: () => void;
  aoConfirmar: (nome: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const confere = texto.trim() === lote.nome.trim();
  const campo = `confirmacao-lote-${lote.id}`;

  useEffect(() => {
    if (!confirmando) {
      setTexto("");
      setErro(null);
    }
  }, [confirmando]);

  return (
    <article className="fonte-folha lote-folha">
      {novo && <span className="lote-folha__novo">novo</span>}
      <h3 className="t-secao lote-folha__nome">{lote.nome}</h3>
      <p className="t-ajuda">
        {lote.itensAtuais}
        {lote.itensAtuais !== lote.totalItens ? ` de ${lote.totalItens}` : ""} itens · ids {lote.faixaInicio}–
        {lote.faixaFim}
      </p>
      <p className="t-ajuda t-2 lote-folha__origem">
        {dataCurta(lote.importadoEm)}
        {lote.origem ? ` · ${lote.origem}` : ""}
      </p>

      {confirmando ? (
        <div className="lote-folha__confirmar">
          <label className="t-ajuda" htmlFor={campo}>
            Apaga os {lote.itensAtuais} itens e as respostas deles, sem volta. Digite{" "}
            <span className="dado">{lote.nome}</span> para confirmar.
          </label>
          <input
            id={campo}
            className="campo__entrada"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          {erro && (
            <p className="campo__erro" role="alert">
              {erro}
            </p>
          )}
          <div className="lote-folha__acoes">
            <button
              type="button"
              className="link-mao link-mao--vermelho"
              disabled={!confere || enviando}
              onClick={() => {
                setEnviando(true);
                setErro(null);
                void aoConfirmar(texto)
                  .catch((e: unknown) => setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível excluir."))
                  .finally(() => setEnviando(false));
              }}
            >
              {enviando ? "apagando…" : "apagar"}
            </button>
            <button type="button" className="link-mao link-mao--tinta" onClick={aoCancelar} disabled={enviando}>
              cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="lote-folha__acoes">
          <button type="button" className="link-mao link-mao--vermelho" onClick={aoPedirExclusao}>
            <Icone glifo={IconeExcluir} papel="acao" tamanho="p" />
            excluir
          </button>
        </div>
      )}
    </article>
  );
}

/**
 * Os passos 1 e 2: dizer sobre o que devem ser as questões e levar o pedido a
 * um modelo de linguagem.
 *
 * O Grifo não gera conteúdo — ele importa. O que faltava era o caminho até o
 * JSON: descrever o assunto, lembrar do formato, acertar o caminho de
 * categoria e descobrir em que id começar. Esta seção monta o pedido inteiro e
 * deixa para o usuário só a frase que só ele sabe dizer. Quantidade, tipos,
 * categoria, dificuldade e nome do lote ficam atrás de "mudar": vêm prontos, e
 * o primeiro lote não precisa de nenhum deles.
 *
 * O `primeiroId` vem do maior `faixaFim` dos lotes que a tela já carregou:
 * nenhuma consulta a mais, e é exatamente o número que evita a colisão que a
 * confirmação teria de resolver deslocando o lote inteiro.
 */
function PedirAUmModelo({
  trilha,
  lotes,
  aoGerar,
}: {
  trilha: string;
  lotes: Lote[];
  aoGerar: (conteudo: string, relatorio: Relatorio) => void;
}) {
  const { limites } = useSessao();
  const tour = useTour();
  const [assunto, setAssunto] = useState("");
  const [opcoes, setOpcoes] = useState(false);
  const [categorias, setCategorias] = useState<CategoriaResumo[]>([]);
  const [categoriaId, setCategoriaId] = useState<string | null>(null);
  const [quantidade, setQuantidade] = useState(limites.pedidoQuantidade);
  const [tipos, setTipos] = useState<TipoItem[]>([...TIPOS_DE_PARTIDA]);
  const [dificuldade, setDificuldade] = useState("");
  const [lote, setLote] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [erroAoCopiar, setErroAoCopiar] = useState(false);
  const [verTexto, setVerTexto] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [erroDaIa, setErroDaIa] = useState<string | null>(null);
  const [estadoDaIa, setEstadoDaIa] = useState<EstadoDaIa | null>(null);
  // Provas e materiais com texto (ADR 043). `null` em `escolhidas` é "ainda
  // não mexi": vale a sugestão pela categoria, que acompanha a troca dela.
  const [materiais, setMateriais] = useState<Fonte[]>([]);
  const [escolhidas, setEscolhidas] = useState<string[] | null>(null);

  useEffect(() => {
    void pedir("fonte.listar")
      .then((r) => setMateriais(r.fontes.filter((f) => f.caracteres > 0)))
      .catch(() => {
        /* sem fontes: o pedido sai sem material, como sempre saiu */
      });
    void pedir("categoria.listar")
      .then((r) => setCategorias(r.categorias))
      .catch(() => {
        /* sem árvore ainda: o pedido manda o modelo escolher o caminho */
      });
    void pedir("ia.estado")
      .then(setEstadoDaIa)
      .catch(() => {
        /* IA indisponível: sobra o "copiar", que é o caminho que sempre funciona */
      });
  }, []);

  const primeiroId = lotes.reduce((maior, l) => Math.max(maior, l.faixaFim), 0) + 1;
  const caminho = categorias.find((c) => c.id === categoriaId)?.caminho ?? [];
  // O nome sugerido acompanha a descrição enquanto ninguém digitou o seu: é
  // sugestão, e o campo continua editável porque é este nome que se digita para
  // excluir o lote em bloco depois (RN-25).
  const nomeDoLote = lote.trim() || loteSugerido(assunto, agoraUtc());

  const temIa = Boolean(estadoDaIa && (estadoDaIa.temChaveDaConta || estadoDaIa.temChaveDoServidor));

  // Sugere o material da categoria escolhida e dos níveis acima dela — o mesmo
  // alcance da fonte na RN-08. Sem categoria, nada vem marcado.
  const sugeridas = materiais
    .filter((f) => caminho.length > 0 && f.categoria.every((parte, i) => caminho[i] === parte))
    .map((f) => f.id);
  const usadas = escolhidas ?? sugeridas;

  const pedido = montarPedidoDeGeracao({
    assunto,
    trilha,
    categoria: caminho,
    quantidade,
    tipos,
    dificuldade: dificuldade.trim() || null,
    lote: nomeDoLote,
    primeiroId,
  });

  async function copiar() {
    setErroAoCopiar(false);
    try {
      await navigator.clipboard.writeText(pedido);
      setCopiado(true);
      tour.avancarSe("copiar");
    } catch {
      // Sem permissão de escrever no clipboard: o texto aparece para copiar à mão.
      setErroAoCopiar(true);
      setVerTexto(true);
      tour.avancarSe("copiar");
    }
  }

  async function gerarAgora() {
    setGerando(true);
    setErroDaIa(null);
    try {
      const r = await pedir("ia.gerarItens", {
        corpo: {
          assunto,
          categoriaId,
          quantidade,
          tipos,
          dificuldade: dificuldade.trim() || null,
          lote: nomeDoLote,
          fontes: usadas,
        },
      });
      aoGerar(r.conteudo, r.relatorio);
    } catch (e) {
      setErroDaIa(e instanceof FalhaDaApi ? e.message : "Não foi possível gerar agora.");
    } finally {
      setGerando(false);
    }
  }

  function alternarTipo(t: TipoItem) {
    setTipos((atuais) => (atuais.includes(t) ? atuais.filter((x) => x !== t) : [...atuais, t]));
    setCopiado(false);
  }

  return (
    <>
      <section className="importar__passo" aria-labelledby="assunto-titulo">
        <h2 className="t-secao" id="assunto-titulo">
          <label htmlFor="pedido-assunto">1 · Sobre o que devem ser as questões</label>
        </h2>
        <input
          id="pedido-assunto"
          className="campo__entrada importar__assunto"
          data-tour="descrever"
          value={assunto}
          onChange={(e) => {
            setAssunto(e.target.value);
            setCopiado(false);
          }}
          placeholder="ex.: controle difuso, efeitos da decisão"
          autoComplete="off"
          onFocus={() => {
            // No ensaio, tocar no campo escreve o exemplo e o passo segue.
            if (!emEnsaio()) return;
            if (!assunto.trim()) setAssunto(ASSUNTO_DO_ENSAIO);
            tour.avancarSe("descrever");
          }}
        />
        <p className="importar__resumo">
          <span className="t-ajuda t-2">
            {quantidade} {quantidade === 1 ? "questão" : "questões"} · {listaDeTipos(tipos)}
          </span>
          <button
            type="button"
            className="link-mao"
            aria-expanded={opcoes}
            aria-controls="pedido-opcoes"
            onClick={() => setOpcoes((v) => !v)}
          >
            {opcoes ? "fechar" : "detalhar"}
          </button>
        </p>

        {opcoes && (
          <div className="importar__opcoes" id="pedido-opcoes">
            <div className="importar__grade">
              <div className="campo">
                <label className="campo__rotulo" htmlFor="pedido-quantidade">
                  Quantidade
                </label>
                <Contador
                  id="pedido-quantidade"
                  rotulo="Quantidade"
                  min={1}
                  max={200}
                  valor={quantidade}
                  aoMudar={(n) => {
                    setQuantidade(n);
                    setCopiado(false);
                  }}
                />
                <p className="campo__ajuda">
                  IDs <span className="dado">{primeiroId}</span> a{" "}
                  <span className="dado">{primeiroId + quantidade - 1}</span>
                </p>
              </div>

              <div className="campo">
                <label className="campo__rotulo" htmlFor="pedido-categoria">
                  Categoria
                </label>
                <SeletorDeCategoria
                  id="pedido-categoria"
                  categorias={categorias}
                  valor={categoriaId}
                  aoMudar={setCategoriaId}
                  rotuloVazio="Deixar o modelo escolher"
                />
                <p className="campo__ajuda">
                  {caminho.length > 0
                    ? `Os itens vão para ${caminho.join(" › ")}.`
                    : "Sem categoria escolhida, o modelo cria o caminho a partir da descrição."}
                </p>
              </div>
            </div>

            {/* Tipos como chips com nomes legíveis — nunca o código interno (ADR 039). */}
            <div className="campo">
              <span className="campo__rotulo" id="pedido-tipos">
                Tipos de item
              </span>
              <div className="caixas-em-linha" role="group" aria-labelledby="pedido-tipos">
                {TIPOS_ITEM.map((t) => (
                  <label key={t} className="caixa-linha">
                    <input
                      type="checkbox"
                      className="so-leitor"
                      checked={tipos.includes(t)}
                      onChange={() => alternarTipo(t)}
                    />
                    <CaixaMao marcada={tipos.includes(t)} />
                    <span className="t-anotacao">{NOME_DO_TIPO_LONGO[t]}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="importar__grade">
              <div className="campo">
                <label className="campo__rotulo" htmlFor="pedido-dificuldade">
                  Dificuldade (opcional)
                </label>
                <input
                  id="pedido-dificuldade"
                  className="campo__entrada"
                  value={dificuldade}
                  onChange={(e) => setDificuldade(e.target.value)}
                  placeholder="média, difícil, nível de banca…"
                />
              </div>

              <div className="campo">
                <label className="campo__rotulo" htmlFor="pedido-lote">
                  Nome do lote
                </label>
                <input
                  id="pedido-lote"
                  className="campo__entrada"
                  value={lote}
                  onChange={(e) => setLote(e.target.value)}
                  placeholder={nomeDoLote}
                />
                <p className="campo__ajuda">É o nome que você digita para excluir este lote em bloco depois.</p>
              </div>
            </div>
          </div>
        )}

        {temIa && materiais.length > 0 && (
          <div className="importar__estilos">
            <span className="campo__rotulo" id="pedido-materiais">
              No estilo de <span className="tinta-suave">· opcional</span>
            </span>
            <div className="importar__chips" role="group" aria-labelledby="pedido-materiais">
              {materiais.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="checkbox"
                  className="importar__chip"
                  aria-checked={usadas.includes(f.id)}
                  onClick={() =>
                    setEscolhidas(usadas.includes(f.id) ? usadas.filter((id) => id !== f.id) : [...usadas, f.id])
                  }
                >
                  <CaixaMao marcada={usadas.includes(f.id)} />
                  {f.titulo}
                </button>
              ))}
            </div>
            <p className="campo__ajuda">Marcadas, as questões geradas seguem o estilo e o nível das suas provas.</p>
          </div>
        )}
      </section>

      <section className="importar__passo" aria-labelledby="pedido-titulo">
        <h2 className="t-secao" id="pedido-titulo">
          2 · Copie o pedido e cole num modelo de IA
        </h2>
        <div className="importar__acoes">
          <button type="button" className="botao botao--secundario" data-tour="copiar" onClick={() => void copiar()}>
            <Icone glifo={copiado ? "check" : "copiar"} papel="acao" />
            {copiado ? "Pedido copiado" : "Copiar o pedido"}
          </button>
          <span className="t-ajuda t-2">ChatGPT, Claude, Gemini: qualquer um serve.</span>
        </div>
        {erroAoCopiar && (
          <p className="campo__ajuda" role="status">
            O navegador não deixou copiar. O pedido está abaixo: selecione e copie à mão.
          </p>
        )}

        {/*
          Gerar aqui dentro é atalho de quem tem chave. Sem chave ele não some:
          diz onde se resolve. Recurso que desaparece quando não está
          configurado é indistinguível de recurso que não existe.
        */}
        <p className="importar__ia-linha">
          {temIa ? (
            <button type="button" className="link-mao" disabled={gerando} onClick={() => void gerarAgora()}>
              {gerando ? <Marca girando className="marca--pequena" /> : <Icone glifo={IconeIa} papel="acao" tamanho="p" />}
              {gerando
                ? `gerando ${quantidade} questões…`
                : usadas.length > 0
                  ? `ou gere aqui, no estilo de ${usadas.length === 1 ? "1 fonte" : `${usadas.length} fontes`}`
                  : "ou gere aqui mesmo"}
            </button>
          ) : (
            <span className="t-ajuda t-2">
              Com uma chave em <Link to="/ajustes?aba=ia">Ajustes › Inteligência artificial</Link>, dá para gerar aqui
              mesmo.
            </span>
          )}
          <button
            type="button"
            className="importar__ver-pedido"
            aria-expanded={verTexto}
            aria-controls="pedido-texto-corpo"
            onClick={() => setVerTexto((v) => !v)}
          >
            <Icone glifo={verTexto ? IconeAbrirMenu : IconeAvancar} papel="acao" tamanho="p" />
            ver o pedido em texto
          </button>
        </p>
        {gerando && (
          <p className="campo__ajuda" role="status">
            O modelo está escrevendo as questões. Isso costuma levar algum tempo — a página pode ficar aberta.
          </p>
        )}
        {erroDaIa && (
          <p className="campo__erro" role="alert">
            {erroDaIa}
          </p>
        )}

        {verTexto && (
          <div className="importar__pedido-texto" id="pedido-texto-corpo">
            <label className="so-leitor" htmlFor="pedido-texto">
              O pedido, em texto
            </label>
            <textarea id="pedido-texto" className="campo__entrada campo__area" readOnly value={pedido} rows={12} />
          </div>
        )}
      </section>
    </>
  );
}
